import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { ChatCircleDots, X, PaperPlaneTilt, Sparkle, ArrowDown } from "@phosphor-icons/react";
import { isTeamDevice } from "@/lib/deviceRole";

const STORAGE_KEY = "hbf_support_session_v1";
const LEGACY_STORAGE_KEYS = ["vocalis_support_session_v1"];

function newSessionId() {
  return "sup_" + Math.random().toString(36).slice(2) + "_" + Date.now();
}

export default function SupportChatWidget() {
  const { pathname } = useLocation();
  // Only show on public marketing/legal/auth pages — never inside /app or /owner
  const hidden = pathname.startsWith("/app") || pathname.startsWith("/owner");
  if (hidden) return null;
  // Never expose the marketing chat widget to team-member devices (agents,
  // supervisors). Those devices are supposed to feel like a locked-down
  // workspace entry — no support chat, no marketing pitch.
  if (isTeamDevice()) return null;
  // Also hide on the login page itself — a clean, distraction-free entry.
  if (pathname === "/login") return null;

  return <SupportChatWidgetInner />;
}

function SupportChatWidgetInner() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => {
    // Read active key first; if missing, migrate from legacy Vocalis-era keys.
    let ex = localStorage.getItem(STORAGE_KEY);
    if (!ex) {
      for (const legacy of LEGACY_STORAGE_KEYS) {
        const v = localStorage.getItem(legacy);
        if (v) { ex = v; localStorage.setItem(STORAGE_KEY, v); localStorage.removeItem(legacy); break; }
      }
    }
    if (ex) return ex;
    const id = newSessionId();
    localStorage.setItem(STORAGE_KEY, id);
    return id;
  });
  const [msgs, setMsgs] = useState([
    { id: "welcome", role: "assistant", text: "Hi! I'm the Hello by Flow support assistant. Ask me about plans, features, or how to set up your team. What can I help with?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    const userId = (crypto.randomUUID && crypto.randomUUID()) || `u-${Date.now()}`;
    setMsgs((m) => [...m, { id: userId, role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/support/chat", { session_id: sessionId, message: text });
      const botId = (crypto.randomUUID && crypto.randomUUID()) || `a-${Date.now()}`;
      setMsgs((m) => [...m, { id: botId, role: "assistant", text: data.reply || "(no reply)" }]);
    } catch (err) {
      console.error("Support chat send failed:", err);
      const errId = (crypto.randomUUID && crypto.randomUUID()) || `e-${Date.now()}`;
      setMsgs((m) => [...m, { id: errId, role: "assistant", text: "Hmm — I'm having trouble responding. Email support@hellobyflow.com and we'll help." }]);
    }
    setLoading(false);
  };

  const quickAsks = [
    "How is Hello by Flow different from Twilio?",
    "What docs do I need for KYC?",
    "How do I add an agent to my team?",
  ];

  return (
    <>
      {/* FAB */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-6 z-40 bg-[var(--accent)] text-white px-4 h-12 flex items-center gap-2 shadow-lg hover:bg-[var(--accent-hover)]"
          data-testid="support-chat-fab"
        >
          <ChatCircleDots size={20} weight="fill" />
          <span className="font-semibold text-[13px]">Ask Vox</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-20 right-6 z-40 w-[380px] max-w-[92vw] h-[560px] max-h-[80vh] bg-white border border-[var(--border)] shadow-2xl flex flex-col"
          data-testid="support-chat-panel"
        >
          {/* Header */}
          <div className="bg-[var(--bg-dark)] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-[var(--accent)] flex items-center justify-center"><Sparkle size={16} weight="fill" color="#fff" /></div>
              <div>
                <div className="font-display font-bold text-[15px] leading-none">Vox</div>
                <div className="font-mono text-[10px] text-[#67E8F9] tracking-widest mt-0.5">HELLO BY FLOW AI · ONLINE</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-[#1E293B]" data-testid="support-chat-close"><X size={16} /></button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto bg-[var(--bg)] p-4 space-y-3" data-testid="support-chat-messages">
            {msgs.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3.5 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[var(--accent)] text-white" : "bg-white border border-[var(--border)]"}`} data-testid={`support-msg-${m.role}-${m.id}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-[var(--border)] px-3.5 py-2.5 flex items-center gap-2">
                  <span className="wave-bar" style={{ height: 12 }} />
                  <span className="wave-bar" style={{ height: 12, animationDelay: "120ms" }} />
                  <span className="wave-bar" style={{ height: 12, animationDelay: "240ms" }} />
                  <span className="text-[11px] text-[var(--text-muted)] ml-1 font-mono">Vox is thinking…</span>
                </div>
              </div>
            )}
          </div>

          {/* Quick asks (only when conversation is fresh) */}
          {msgs.length <= 1 && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5 bg-[var(--bg)]">
              {quickAsks.map((q) => (
                <button
                  key={q}
                  onClick={() => { setInput(q); setTimeout(() => send(), 50); }}
                  className="text-[11px] px-2.5 py-1 border border-[var(--border)] bg-white hover:bg-[var(--bg-sidebar)]"
                  data-testid={`quick-ask-${q.slice(0, 20).replace(/[^a-z0-9]/gi, "-")}`}
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Composer */}
          <form onSubmit={send} className="border-t border-[var(--border)] p-3 flex gap-2 bg-white">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything about Hello by Flow…"
              className="input"
              disabled={loading}
              data-testid="support-chat-input"
            />
            <button type="submit" disabled={loading || !input.trim()} className="btn-primary px-3" data-testid="support-chat-send">
              <PaperPlaneTilt size={16} weight="fill" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
